import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { Compass } from 'lucide-react';
import { AppShell } from '@/app/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { ErrorBoundary, ErrorState } from '@/components/ErrorState';
import { LoginPage } from '@/features/auth/LoginPage';
import { AzureCallbackPage } from '@/features/auth/AzureCallbackPage';
import { PendingApprovalPage } from '@/features/auth/PendingApprovalPage';
import { useAuthStore } from '@/features/auth/auth.store';
import { DiagnosticsPage } from '@/features/diagnostics/DiagnosticsPage';
import { HomePage } from '@/features/home/HomePage';
import { criarRotasDeAdministracao } from '@/features/admin/routes';
import { criarRotasDeMudancas } from '@/features/changes/routes';
import { criarRotasDeClientes } from '@/features/clientes/routes';
import { ContratosDashboardPage } from '@/features/contratos/ContratosDashboardPage';
import { criarRotasDeContratos } from '@/features/contratos/routes';
import { criarRotasDeMensagensRetidas } from '@/features/email/routes';
import { criarRotasDeErrosConhecidos } from '@/features/kedb/routes';
import { criarRotasDeConhecimento } from '@/features/knowledge/routes';
import { criarRotasDeRelatorios } from '@/features/relatorios/routes';
import { criarRotasDeEntregas } from '@/features/releases/routes';
import { criarRotasDeChamados } from '@/features/tickets/routes';
import { criarRotasDeFluxos } from '@/features/workflow/routes';
import { criarRotasDeProjetos } from '@/features/projetos/routes';
import { MelhoriasCsiPage } from '@/features/csi/MelhoriasCsiPage';
import { CmdbPage } from '@/features/cmdb/CmdbPage';
import { CatalogoPage } from '@/features/catalog/CatalogoPage';
import { WorkloadPage } from '@/features/projetos/WorkloadPage';
import { PortfolioPage } from '@/features/projetos/PortfolioPage';
import { RelatorioAlocacaoPage } from '@/features/projetos/RelatorioAlocacaoPage';
import { SprintsPage } from '@/features/projetos/SprintsPage';
import { OKRsPage } from '@/features/okrs/OKRsPage';

// ─────────────────────────────────────────────────────────────────────────────
// PONTO DE EXTENSÃO — rotas de feature
//
// As rotas de chamado e as de administração vêm das respectivas features como
// **fábrica**: `createRoute` exige a rota pai, e o pai é o `appRoute` daqui —
// receber por parâmetro evita que a feature importe de dentro de `app/`, o que
// fecharia um ciclo entre o roteador e a tela que ele monta.
// ─────────────────────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: Outlet,
  errorComponent: ErroDeRota,
  notFoundComponent: NaoEncontrado,
});

// ── autenticação ───────────────────────────────────────────────────────────

/**
 * Só aceita caminho interno.
 *
 * O destino chega pela URL, então é entrada do usuário — e sem esta conferência
 * `?destino=https://outro.site` transformaria a tela de login num redirecionador
 * aberto, que é exatamente o que se usa em phishing. `//` também sai: o
 * navegador o lê como protocolo relativo e sai do domínio.
 */
function caminhoInterno(valor: unknown): string | null {
  if (typeof valor !== 'string') return null;
  if (!valor.startsWith('/') || valor.startsWith('//')) return null;
  return valor;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entrar',
  validateSearch: (search: Record<string, unknown>): { destino?: string; erro?: string } => {
    const destino = caminhoInterno(search['destino']);
    const erro = typeof search['erro'] === 'string' ? search['erro'] : undefined;
    return { ...(destino ? { destino } : {}), ...(erro ? { erro } : {}) };
  },
  beforeLoad: ({ search }) => {
    // Quem já tem sessão não vê o formulário — nem digitando `/entrar` à mão.
    if (useAuthStore.getState().status === 'authenticated') {
      throw redirect({ href: search.destino ?? '/' });
    }
  },
  component: TelaDeLogin,
});

const azureCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/azure/ok',
  validateSearch: (search: Record<string, unknown>): { code?: string; destino?: string } => ({
    code: typeof search['code'] === 'string' ? search['code'] : undefined,
    destino: caminhoInterno(search['destino']) ?? undefined,
  }),
  component: AzureCallbackPage,
});

const pendingApprovalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/pendente',
  component: PendingApprovalPage,
});

function TelaDeLogin() {
  const { destino } = loginRoute.useSearch();
  return <LoginPage destino={destino ?? '/'} />;
}

/**
 * Tudo o que exige sessão pendura aqui.
 *
 * O guard é de **experiência**, não de segurança: quem esconde dado é a API.
 * Redirecionar sem token evita mostrar uma casca vazia que falharia na
 * primeira requisição — e preserva o destino, para que voltar do login caia
 * na tela que a pessoa pediu, não numa página inicial genérica.
 *
 * Roda em `beforeLoad`, e não dentro do componente, porque redirecionar
 * durante a renderização deixa a tela protegida piscar antes de sair.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ location }) => {
    if (useAuthStore.getState().status === 'authenticated') return;
    throw redirect({ to: '/entrar', search: { destino: location.href } });
  },
  component: CascaProtegida,
});

function CascaProtegida() {
  const pathname = useRouterState({ select: (estado) => estado.location.pathname });
  return (
    <AppShell>
      {/* A chave zera o boundary a cada rota: sem isso, uma tela que falhou
          prenderia todas as seguintes na mesma mensagem de erro. */}
      <ErrorBoundary resetKey={pathname}>
        <Outlet />
      </ErrorBoundary>
    </AppShell>
  );
}

// ── telas ──────────────────────────────────────────────────────────────────

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  component: HomePage,
});

const diagnosticoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/diagnostico',
  component: DiagnosticsPage,
});

const { lista: chamadosRoute, detalhe: chamadoRoute } = criarRotasDeChamados(appRoute);

/**
 * Clientes: duas rotas irmãs, e não uma raiz com filhas.
 *
 * Lista e ficha não compartilham casca — a ficha é uma tela inteira, com
 * cabeçalho próprio e caminho de volta —, então a fábrica devolve as duas,
 * como a de chamados. Quantos campos cada uma mostra é decisão da feature.
 */
const { lista: clientesRoute, detalhe: clienteRoute } = criarRotasDeClientes(appRoute);

/**
 * Contratos ficam ao lado de Clientes, e não dentro deles.
 *
 * Um contrato pertence a um cliente, mas quem o abre não está olhando a
 * carteira: está conferindo cobertura, franquia e vencimento. Aninhá-lo na
 * ficha do cliente obrigaria a passar por ela toda vez que a pergunta é sobre
 * o contrato.
 */
const { lista: contratosRoute, detalhe: contratoRoute } = criarRotasDeContratos(appRoute);

// Dashboard de contratos — rota estática antes da dinâmica `$contratoId`.
const contratosDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/contratos/dashboard',
  component: ContratosDashboardPage,
});

/**
 * A fila de mensagens retidas: uma rota só, sem filha de detalhe.
 *
 * A mensagem abre numa janela sobre a lista porque a fila é para ser drenada em
 * sequência — navegar e voltar perderia a posição a cada decisão.
 */
const retidasRoute = criarRotasDeMensagensRetidas(appRoute);

const catalogoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/catalogo',
  component: CatalogoPage,
});

const relatoriosRoute = criarRotasDeRelatorios(appRoute);

/**
 * A administração vem inteira da fábrica — com as abas já penduradas.
 *
 * Quantos catálogos existem e em que ordem aparecem é decisão da feature; aqui
 * só se diz onde ela mora na árvore. Um catálogo novo não precisa tocar neste
 * arquivo.
 */
const administracaoRoute = criarRotasDeAdministracao(appRoute);

/**
 * Fluxos ficam fora de `/administracao` de propósito.
 *
 * Aquela tela administra **catálogos de vocabulário** — situação, categoria,
 * equipe —, listas planas editadas em linha. Um fluxo é um documento
 * versionado, com rascunho, validação e publicação, e é a única coisa nesta
 * aplicação que muda o caminho de todo chamado novo de um tipo. Empurrá-lo
 * para dentro de uma aba de tabela daria a ele a cara de mais um cadastro.
 */
const { lista: fluxosRoute, detalhe: fluxoRoute } = criarRotasDeFluxos(appRoute);
const { lista: projetosRoute, detalhe: projetoRoute, minhasAtividades: minhasAtividadesRoute } = criarRotasDeProjetos(appRoute);

const cargaTrabalhoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/carga-trabalho',
  component: WorkloadPage,
});

const portfolioRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/portfolio',
  component: PortfolioPage,
});

const relatorioAlocacaoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/relatorio-alocacao',
  component: RelatorioAlocacaoPage,
});

const sprintsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sprints',
  component: SprintsPage,
});

const okrsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/okrs',
  component: OKRsPage,
});

const melhoriasCsiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/melhorias',
  component: MelhoriasCsiPage,
});

const cmdbRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cmdb',
  component: CmdbPage,
});

/**
 * Change Enablement, Release Management e KEDB — as três práticas da Fase 4.
 *
 * O calendário de mudanças vem inteiro da fábrica, com as abas já penduradas —
 * como a administração e os relatórios. As versões de entrega e os erros
 * conhecidos devolvem duas rotas irmãs cada, porque lista e ficha não
 * compartilham casca.
 *
 * **A ficha da mudança não está aqui**: ela é uma aba do chamado, porque a
 * mudança é extensão 1:1 do chamado (ADR-001). Dar a ela endereço próprio
 * criaria duas páginas para o mesmo registro.
 */
const mudancasRoute = criarRotasDeMudancas(appRoute);
const { lista: entregasRoute, detalhe: entregaRoute } = criarRotasDeEntregas(appRoute);
const { lista: errosRoute, detalhe: erroRoute } = criarRotasDeErrosConhecidos(appRoute);

/**
 * Base de conhecimento — Fase 8. Quatro rotas irmãs.
 *
 * Lista, ficha e editor não compartilham casca: a ficha é uma tela inteira e o
 * editor é outra, com duas colunas e pré-visualização.
 *
 * `/conhecimento/novo` vem antes de `/conhecimento/$artigoId` na montagem
 * abaixo. O TanStack já ordena rota estática antes de dinâmica, mas depender
 * disso é depender de detalhe de implementação — e "novo" lido como
 * identificador abriria uma ficha 404 em vez do editor.
 */
const {
  lista: conhecimentoRoute,
  novo: artigoNovoRoute,
  detalhe: artigoRoute,
  editar: artigoEditarRoute,
} = criarRotasDeConhecimento(appRoute);

// ── erro e 404 ─────────────────────────────────────────────────────────────

/**
 * Link com aparência de botão.
 *
 * Um `<Button>` dentro de um `<Link>` aninharia dois controles interativos: o
 * leitor de tela anuncia os dois e o Tab para em cada um. Como o destino é
 * uma navegação, o elemento certo é a âncora — só o visual é de botão.
 */
const CLASSE_DE_BOTAO =
  'inline-flex h-8 items-center justify-center rounded-md border border-border bg-secondary ' +
  'px-3 text-sm font-medium text-secondary-foreground transition-colors hover:bg-accent ' +
  'focus-visible:shadow-glow focus-visible:outline-none';

function ErroDeRota({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="grid min-h-full place-items-center bg-background px-4 py-12">
      <div className="w-full max-w-lg space-y-4">
        <ErrorState
          error={error}
          title="A aplicação não conseguiu abrir esta rota"
          onRetry={reset}
        />
        <div className="text-center">
          <Link to="/" className={CLASSE_DE_BOTAO}>
            Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  );
}

function NaoEncontrado() {
  return (
    <div className="grid min-h-full place-items-center bg-background px-4 py-12">
      <div className="w-full max-w-lg">
        <EmptyState
          icon={<Compass className="h-7 w-7" aria-hidden />}
          title="Esta página não existe"
          description="O endereço pode ter mudado, ou a tela ainda não foi construída."
          action={
            <Link to="/" className={CLASSE_DE_BOTAO}>
              Voltar ao início
            </Link>
          }
        />
      </div>
    </div>
  );
}

// ── montagem ───────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  loginRoute,
  azureCallbackRoute,
  pendingApprovalRoute,
  appRoute.addChildren([
    indexRoute,
    chamadosRoute,
    chamadoRoute,
    mudancasRoute,
    entregasRoute,
    entregaRoute,
    errosRoute,
    erroRoute,
    conhecimentoRoute,
    artigoNovoRoute,
    artigoRoute,
    artigoEditarRoute,
    clientesRoute,
    clienteRoute,
    contratosRoute,
    contratosDashboardRoute,
    contratoRoute,
    retidasRoute,
    catalogoRoute,
    fluxosRoute,
    fluxoRoute,
    projetosRoute,
    projetoRoute,
    minhasAtividadesRoute,
    cargaTrabalhoRoute,
    portfolioRoute,
    relatorioAlocacaoRoute,
    sprintsRoute,
    okrsRoute,
    melhoriasCsiRoute,
    cmdbRoute,
    relatoriosRoute,
    administracaoRoute,
    diagnosticoRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultErrorComponent: ErroDeRota,
  defaultNotFoundComponent: NaoEncontrado,
});

/**
 * O `beforeLoad` só roda em navegação, e sair não é navegar.
 *
 * Sem este aviso ao roteador, quem clica em "Sair" continuaria olhando a tela
 * protegida — agora sem dado nenhum — até tentar ir a outro lugar.
 */
useAuthStore.subscribe((estado, anterior) => {
  if (estado.status !== anterior.status) void router.invalidate();
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
